/* The knowledge base's signup form, wired only when it is about to matter.
 *
 * The form sits at the foot of every article, and the scripts that drive it
 * (js/landing.js and the modules it is built from) are most of the page's
 * JavaScript. Most readers never scroll that far, so the page carries them as
 * inert tags (`type="text/x-lazy"`, `data-chart-form`) and this file injects
 * them, in order, the first time any of these happens:
 *
 * - the form comes within a few screens of the viewport;
 * - a field in it takes focus or a pointer;
 * - it is submitted, or one of its sign-up buttons is clicked, before the
 *   scripts arrived — that action is held and replayed once they have.
 *
 * Two things landing.js would have recorded from page load are recorded here
 * instead and read back by it: the form being seen (the `viewed` step of
 * signup_form_events, held to the same IntersectionObserver rule landing.js
 * uses, so the step fires on the same scroll as before) and a click on the
 * article's "jump to the form" link (the `jumped` step).
 *
 * If landing.js cannot be fetched, the failure is reported and the next
 * interaction retries; a submit or sign-up click that still cannot load it
 * goes to the app's own signup through /go/, as landing.js does when
 * PersonForm is missing. Never a form that silently does nothing.
 */
(function () {
  window.GHD = window.GHD || {};
  var GHD = window.GHD;

  // The knowledge base used to register the service worker through landing.js,
  // which it now loads late or not at all. Same call, same reason for the catch.
  if ("serviceWorker" in navigator && location.hostname !== "localhost") {
    navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).catch(function () {});
  }

  var section = document.getElementById("chart-form");
  var form = document.getElementById("chart-form-el");
  if (!section || !form) return;

  var pending = Array.prototype.map.call(
    document.querySelectorAll('script[type="text/x-lazy"][data-chart-form]'),
    function (tag) {
      return tag.getAttribute("data-src");
    },
  );
  var landingSrc = pending[pending.length - 1];
  var loading = null;
  var ready = false;
  var reported = false;
  var holding = false;

  function inject(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src;
      // Injected scripts are async by default; these must run in the order
      // listed, because each module reads the ones before it.
      s.async = false;
      s.onload = function () {
        resolve();
      };
      s.onerror = function () {
        s.remove();
        reject(new Error("Failed to load " + src));
      };
      document.head.appendChild(s);
    });
  }

  /** Inject what has not arrived yet. Resolves once landing.js has run. */
  function load() {
    if (ready) return Promise.resolve();
    if (loading) return loading;
    var batch = pending;
    pending = [];
    var failed = [];
    loading = Promise.all(
      batch.map(function (src) {
        return inject(src).catch(function () {
          failed.push(src);
        });
      }),
    ).then(function () {
      // A module that failed is landing.js's to degrade around (GHD.moduleOr);
      // only landing.js itself missing leaves the form dead.
      pending = batch.filter(function (src) {
        return failed.indexOf(src) !== -1;
      });
      loading = null;
      if (failed.indexOf(landingSrc) === -1) {
        ready = true;
        return;
      }
      if (!reported && GHD.reportClientError) {
        reported = true;
        GHD.reportClientError("reported", "chart form scripts failed to load", "missing: " + failed.join(","), null);
      }
      throw new Error("chart form scripts failed to load");
    });
    return loading;
  }

  function loadQuietly() {
    load().catch(function () {
      /* Reported in load(); the next interaction tries again. */
    });
  }

  /** Hold an action taken before the form was wired, and replay it once it is. */
  function hold(e, replay) {
    if (ready) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (holding) return;
    holding = true;
    load().then(
      function () {
        holding = false;
        replay();
      },
      function () {
        window.location.href = "/go/?to=" + encodeURIComponent("/signup");
      },
    );
  }

  /* Readers, not only form-fillers (migration 195): `read` once a signed-out
   * reader has had the article visible for READ_AFTER_MS and has scrolled, and
   * `asked` if the question after the intro was in view too. Both wait for
   * `read`, so a crawler that renders the page and leaves sends neither.
   * landing.js reuses the visit id and the page, so one page load's steps
   * group together. */
  var READ_AFTER_MS = 10000;
  var SCROLLED_PX = 200;
  var api = typeof GHD_APP !== "undefined" && GHD_APP.API_BASE_URL;
  var pageMatch = /\/docs\/(?:(.*?)\/?)$/.exec(location.pathname || "");
  var page = pageMatch ? pageMatch[1] || "index" : null;
  GHD.chartFormPage = page && /^[a-z0-9-]+(\/[a-z0-9-]+)*$/.test(page) ? page : null;
  GHD.chartFormVisit =
    window.crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now();

  function send(step) {
    try {
      fetch(api + "/v1/signup-form-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          visit: GHD.chartFormVisit,
          step: step,
          surface: "kb",
          locale: document.documentElement.lang || "en",
          page: GHD.chartFormPage,
        }),
      }).catch(function () {});
    } catch (e) {
      /* a lost step is a gap in a count */
    }
  }

  function watchReader() {
    // `make serve` writes to production's database, so a local read would count.
    if (!api || location.hostname === "localhost" || location.hostname === "127.0.0.1") return;
    var scrolled = false;
    var timeUp = false;
    var read = false;
    var asked = false;
    var questionSeen = false;
    var left = READ_AFTER_MS;
    var since = 0;
    var timer = null;

    function maybeRead() {
      // auth-nav.js hides the form for a signed-in reader, who is not in this funnel.
      if (!read && scrolled && timeUp && !section.hidden) {
        read = true;
        send("read");
      }
      if (read && questionSeen && !asked) {
        asked = true;
        send("asked");
      }
    }
    function onScroll() {
      if ((window.scrollY || 0) < SCROLLED_PX) return;
      scrolled = true;
      window.removeEventListener("scroll", onScroll);
      maybeRead();
    }
    // The clock runs only while the tab is visible: a background tab is not a reader.
    function onVisibility() {
      if (timeUp) return;
      if (document.visibilityState === "hidden") {
        if (!timer) return;
        clearTimeout(timer);
        timer = null;
        left -= Date.now() - since;
      } else if (!timer) {
        since = Date.now();
        timer = setTimeout(function () {
          timer = null;
          timeUp = true;
          maybeRead();
        }, left);
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    onVisibility();

    var question = document.querySelector(".docs-cta-inline");
    if (question && "IntersectionObserver" in window) {
      var questionIo = new IntersectionObserver(
        function (entries) {
          // The first callback reports any overlap at all, whatever the threshold.
          if (
            !entries.some(function (e) {
              return e.isIntersecting && e.intersectionRatio >= 0.5;
            })
          )
            return;
          questionIo.disconnect();
          questionSeen = true;
          maybeRead();
        },
        { threshold: 0.5 },
      );
      questionIo.observe(question);
    }
  }

  function start() {
    GHD.domContentLoaded = true;
    watchReader();

    form.addEventListener(
      "submit",
      function (e) {
        hold(e, function () {
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event("submit", { cancelable: true }));
        });
      },
      true,
    );
    form.addEventListener(
      "click",
      function (e) {
        var btn = e.target && e.target.closest && e.target.closest("[data-auth-provider], #cf-email-toggle");
        if (!btn) return;
        hold(e, function () {
          btn.click();
        });
      },
      true,
    );
    ["focusin", "pointerdown"].forEach(function (evt) {
      form.addEventListener(evt, loadQuietly, { capture: true, passive: true });
    });

    document.addEventListener("click", function (e) {
      var link = e.target && e.target.closest && e.target.closest('a[href="#chart-form"]');
      if (!link) return;
      GHD.chartFormJumped = true;
      loadQuietly();
    });

    if (!("IntersectionObserver" in window)) {
      // landing.js counts no `viewed` without one either.
      loadQuietly();
      return;
    }
    // Generous, so the scripts have run before a reader scrolling at speed
    // reaches the form and the date fields never change shape in view.
    var nearIo = new IntersectionObserver(
      function (entries) {
        if (
          !entries.some(function (e) {
            return e.isIntersecting;
          })
        )
          return;
        nearIo.disconnect();
        loadQuietly();
      },
      { rootMargin: "0px 0px 300% 0px" },
    );
    nearIo.observe(section);
    // landing.js's own rule for `viewed`, kept from page load so a reader who
    // reaches the form before landing.js does is still counted.
    var seenIo = new IntersectionObserver(
      function (entries) {
        if (
          !entries.some(function (e) {
            return e.isIntersecting;
          })
        )
          return;
        seenIo.disconnect();
        GHD.chartFormSeen = true;
        loadQuietly();
      },
      { threshold: 0.3 },
    );
    seenIo.observe(form);
  }

  // After DOMContentLoaded, never during the deferred chain: landing.js runs its
  // handlers at once when told that event has passed (GHD.onDomReady).
  if (document.readyState === "complete") start();
  else document.addEventListener("DOMContentLoaded", start);
})();
